import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ADDRESS_DATA_BASE_PATH } from '@app/shared/constants/communications';
import { StreetType } from '@app/shared/interfaces/common-data/street-type';
import { catchError, EMPTY, map, Observable } from 'rxjs';
import { Province } from '@app/shared/interfaces/common-data/province';
import { PostalCode } from '@app/shared/interfaces/common-data/postal-code';
import { Country } from '@app/shared/interfaces/common-data/country';
import { AppConfigService as appConfig } from '@app/app.config';

@Injectable({
    providedIn: 'root',
})
export class AddressDataService {
    private http = inject(HttpClient);
    private appConfig = inject(appConfig);

    /**
     * Returns a list of all countries from CommonsBusiness:AddresData API.
     */
    getCountries(): Observable<Country[]> {
        const COUNTRIES_URL: string = this.appConfig.apiBffUrl + ADDRESS_DATA_BASE_PATH + '/countries';
        return this.http.get<Country[]>(COUNTRIES_URL).pipe(catchError(() => EMPTY));
    }

    /**
     * Returns a list of all street types from CommonsBusiness:AddresData API.
     */
    getStreetTypes(): Observable<StreetType[]> {
        const STREETYPE_URL: string = this.appConfig.apiBffUrl + ADDRESS_DATA_BASE_PATH + '/street-types';
        return this.http.get<StreetType[]>(STREETYPE_URL).pipe(catchError(() => EMPTY));
    }

    /**
     * Returns a list of all provinces from CommonsBusiness:AddresData API.
     */
    getProvinces(): Observable<Province[]> {
        const PROVINCE_URL: string = this.appConfig.apiBffUrl + ADDRESS_DATA_BASE_PATH + '/provinces';
        return this.http.get<Province[]>(PROVINCE_URL).pipe(catchError(() => EMPTY));
    }

    /**
     * Returns a list of all cities by province name from CommonsBusiness:AddresData API.
     */
    getLocations(province: string): Observable<string[]> {
        return this.getPostalCodes(province).pipe(map((postalCodes) => postalCodes.map((postalCode) => postalCode.location)));
    }

    /**
     * Returns a list of all postal codes by province from CommonsBusiness:AddresData API.
     */
    getPostalCodes(province: string): Observable<PostalCode[]> {
        const POSTALCODE_URL: string = this.appConfig.apiBffUrl + ADDRESS_DATA_BASE_PATH + `/postal-codes?province=${province}`;
        return this.http.get<PostalCode[]>(POSTALCODE_URL).pipe(catchError(() => EMPTY));
    }
}
